import { isNil } from '../../common'
import { Action, BranchExecutionType, RouterAction } from '../actions/action'
import { FlowVersion } from '../flow-version'
import { flowStructureUtil } from '../util/flow-structure-util'
import { addActionUtls } from './add-action-util'
import { _getImportOperations } from './import-flow'
import { FlowOperationRequest, FlowOperationType, StepLocationRelativeToParent } from '.'

function calculateDuplicatePosition(
    stepName: string,
    flowVersion: FlowVersion,
): { x: number, y: number } | undefined {
    const originalStep = flowStructureUtil.getActionOrThrow(stepName, flowVersion)
    
    if (!originalStep.settings?.position) {
        return undefined
    }
    
    const { x, y } = originalStep.settings.position
    const hasTarget = flowStructureUtil.getStepTargets(flowVersion, stepName).length > 0
    const routerParent = flowStructureUtil.getParentRouterStepNameAndBranchIndex(flowVersion, stepName)
    let branchType: 'CONDITION' | 'FALLBACK' | null = null
    const DUPLICATE_OFFSET = 200
    
    if (routerParent) {
        const router = flowStructureUtil.getActionOrThrow(routerParent.routerParentName, flowVersion) as RouterAction
        const branchConfig = router.settings.branches[routerParent.branchIndex]
        branchType = branchConfig?.branchType || null
    }
    
    if (!hasTarget) {
        return { x: x + DUPLICATE_OFFSET, y }
    }

    if (branchType === 'FALLBACK') {
        return { x, y: y + DUPLICATE_OFFSET }
    }

    return { x, y: y - DUPLICATE_OFFSET }
}

function _duplicateStep(stepName: string, flowVersion: FlowVersion): FlowOperationRequest[] {
    const clonedAction: Action = JSON.parse(JSON.stringify(flowStructureUtil.getActionOrThrow(stepName, flowVersion)))
    const clonedActionWithoutNextAction = {
        ...clonedAction,
        nextAction: undefined,
    }
    const oldNameToNewName = addActionUtls.mapToNewNames(flowVersion, [clonedActionWithoutNextAction])
    const clonedSubflow = flowStructureUtil.transferStep(clonedActionWithoutNextAction, (step: Action) => {
        return addActionUtls.clone(step, oldNameToNewName)
    })
    const newStepPosition = calculateDuplicatePosition(stepName, flowVersion)

    return [
        {
            type: FlowOperationType.ADD_ACTION,
            request: {
                action: clonedSubflow as Action,
                parentStep: [stepName],
                stepLocationRelativeToParent: StepLocationRelativeToParent.AFTER,
                newStepPosition,
            },
        }
    ]
}

function _duplicateBranch(
    routerName: string,
    childIndex: number,
    flowVersion: FlowVersion,
): FlowOperationRequest[] {
    const router = flowStructureUtil.getActionOrThrow(routerName, flowVersion)
    const clonedRouter: RouterAction = JSON.parse(JSON.stringify(router))
    const operations: FlowOperationRequest[] = [{
        type: FlowOperationType.ADD_BRANCH,
        request: {
            branchName: `${clonedRouter.settings.branches[childIndex].branchName} Copy`,
            branchIndex: childIndex + 1,
            stepName: routerName,
            conditions: clonedRouter.settings.branches[childIndex].branchType === BranchExecutionType.CONDITION ? clonedRouter.settings.branches[childIndex].conditions : undefined,
        },
    }]

    const childRouter = clonedRouter.children[childIndex]
    if (!isNil(childRouter)) {
        const oldNameToNewName = addActionUtls.mapToNewNames(flowVersion, childRouter.actions as Action[])
        
        const tempFlowVersion: FlowVersion = {
            ...flowVersion,
            actions: childRouter.actions.map(action => {
                const clonedAction = JSON.parse(JSON.stringify(action)) as Action
                if (oldNameToNewName[clonedAction.name]) {
                    clonedAction.name = oldNameToNewName[clonedAction.name]
                }
                if (clonedAction.settings.position) {
                    clonedAction.settings.position = {
                        ...clonedAction.settings.position,
                        y: clonedAction.settings.position.y - 100
                    }
                }
                return clonedAction
            }),
            connections: childRouter.connections.map(conn => {
                if (!conn.sourceId || !conn.targetId) {
                    return { sourceId: '', targetId: '' }
                }

                const getMappedId = (id: string) => {
                    if (id === routerName) {
                        return id;
                    }
                    return oldNameToNewName[id] || id;
                };

                return {
                    sourceId: getMappedId(conn.sourceId),
                    targetId: getMappedId(conn.targetId)
                }
            })
        }
        const importOperations = _getImportOperations(tempFlowVersion, {
            routerStep: router as RouterAction,
            branchIndex: childIndex + 1,
        })
        
        operations.push(...importOperations)
    }

    return operations
}

export { _duplicateStep, _duplicateBranch }
